import { Plus, RefreshCw, Search } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import { authoredIconTextPrefix } from '@/components/admin/shared/authored-icon-text'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSources } from '../sources/hooks/use-sources'
import { useTasks } from './hooks/use-tasks'
import { TaskEditorModal } from './task-editor-modal'
import { TasksTable } from './tasks-table'
import type { Task, TaskFormData } from './types'
import { TASK_CATEGORIES } from './types'

export function TasksAdmin() {
  const { tasks, loading, createTask, updateTask, deleteTask, refresh } =
    useTasks()
  const { sources } = useSources()

  const [searchQuery, setSearchQuery] = useState('')
  const [filterSourceId, setFilterSourceId] = useState<string | null>(null)
  const [filterCategory, setFilterCategory] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)

  // Filter tasks by search query, source, and category
  const filteredTasks = tasks.filter((task) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const matchesSearch =
        task.title.toLowerCase().includes(query) ||
        task.notes?.toLowerCase().includes(query) ||
        task.category?.toLowerCase().includes(query)
      if (!matchesSearch) return false
    }

    // Source filter
    if (filterSourceId && task.sourceId !== filterSourceId) {
      return false
    }

    // Category filter
    if (filterCategory && task.category !== filterCategory) {
      return false
    }

    return true
  })

  const handleOpenCreate = useCallback(() => {
    setEditingTask(null)
    setIsModalOpen(true)
  }, [])

  const handleOpenEdit = useCallback((task: Task) => {
    setEditingTask(task)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setEditingTask(null)
  }, [])

  const handleSubmit = useCallback(
    async (data: TaskFormData): Promise<Task> => {
      if (editingTask) {
        const updated = await updateTask(editingTask.id, data)
        toast.success('Task updated')
        return updated
      } else {
        const created = await createTask(data)
        toast.success('Task created')
        return created
      }
    },
    [editingTask, createTask, updateTask],
  )

  const handleDelete = useCallback(
    async (task: Task) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete task "${task.title}"?`)) return
      try {
        await deleteTask(task.id)
        toast.success('Task deleted')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete')
      }
    },
    [deleteTask],
  )

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-foreground text-2xl font-bold">Tasks</h1>
              <p className="text-muted-foreground text-sm">
                Manage reusable task templates for coaching homework
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={refresh}
                disabled={loading}
              >
                <RefreshCw
                  size={16}
                  className={loading ? 'animate-spin' : ''}
                />
              </Button>
              <Button onClick={handleOpenCreate}>
                <Plus size={16} className="mr-1" />
                Add Task
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="border-border border-b">
        <div className="mx-auto max-w-6xl px-6 py-3">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search
                size={16}
                className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Source filter */}
            <select
              value={filterSourceId || ''}
              onChange={(e) => setFilterSourceId(e.target.value || null)}
              className="border-input bg-background h-9 rounded-md border px-3 text-sm"
            >
              <option value="">All sources</option>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {authoredIconTextPrefix(source.icon)}
                  {source.name}
                </option>
              ))}
            </select>

            {/* Category filter */}
            <select
              value={filterCategory || ''}
              onChange={(e) => setFilterCategory(e.target.value || null)}
              className="border-input bg-background h-9 rounded-md border px-3 text-sm"
            >
              <option value="">All categories</option>
              {TASK_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Clear filters */}
            {(searchQuery || filterSourceId || filterCategory) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('')
                  setFilterSourceId(null)
                  setFilterCategory(null)
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-6 py-6">
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border flex items-center justify-between border-b p-4">
            <span className="text-muted-foreground text-sm">
              {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
              {filteredTasks.length !== tasks.length &&
                ` (${tasks.length} total)`}
            </span>
          </div>

          <TasksTable
            tasks={filteredTasks}
            loading={loading}
            onEdit={handleOpenEdit}
            onDelete={handleDelete}
          />
        </div>
      </main>

      {/* Task Editor Modal */}
      <TaskEditorModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        task={editingTask}
      />
    </div>
  )
}
